/**
 * Cub, the CyberCub mascot.
 *
 * `avatar` picks the fur colour ("cub-teal", "cub-coral", ...) and is
 * also what gets stored on the user row, so a child keeps the same
 * cub everywhere. `mood` swaps the face and the little accessory.
 */
const FUR = {
  'cub-teal':   { body: '#17c3c3', dark: '#0d9a9a' },
  'cub-sky':    { body: '#29b6f6', dark: '#0d8ecf' },
  'cub-coral':  { body: '#ff6b6b', dark: '#dd4444' },
  'cub-mint':   { body: '#22c58b', dark: '#159a69' },
  'cub-sun':    { body: '#ffc93c', dark: '#e0a413' },
  'cub-grape':  { body: '#7c4dff', dark: '#5b32d6' },
  'cub-bubble': { body: '#ff7eb6', dark: '#e05593' },
};

export const AVATAR_OPTIONS = Object.keys(FUR);

export default function CubMascot({
  avatar = 'cub-teal',
  mood = 'happy',
  size = 120,
  className = '',
  title,
}) {
  const fur = FUR[avatar] ?? FUR['cub-teal'];
  const sad = mood === 'sad';
  const wink = mood === 'wave' || mood === 'cheer';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={className}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : 'true'}
    >
      {/* ears */}
      <circle cx="30" cy="30" r="15" fill={fur.dark} stroke="#2d2a45" strokeWidth="3.5" />
      <circle cx="90" cy="30" r="15" fill={fur.dark} stroke="#2d2a45" strokeWidth="3.5" />
      <circle cx="30" cy="30" r="6.5" fill="#ffd9c9" />
      <circle cx="90" cy="30" r="6.5" fill="#ffd9c9" />

      {/* head */}
      <circle cx="60" cy="62" r="38" fill={fur.body} stroke="#2d2a45" strokeWidth="3.5" />

      {/* muzzle */}
      <ellipse cx="60" cy="76" rx="21" ry="16" fill="#fff4e6" stroke="#2d2a45" strokeWidth="3" />
      <ellipse cx="60" cy="68" rx="6.5" ry="5" fill="#2d2a45" />

      {/* mouth */}
      {sad ? (
        <path d="M50 84 Q60 77 70 84" stroke="#2d2a45" strokeWidth="3" fill="none" strokeLinecap="round" />
      ) : (
        <path d="M60 73 v5 M60 78 Q52 86 46 79 M60 78 Q68 86 74 79"
          stroke="#2d2a45" strokeWidth="3" fill="none" strokeLinecap="round" />
      )}

      {/* eyes */}
      {wink ? (
        <>
          <path d="M40 55 q5 -6 10 0" stroke="#2d2a45" strokeWidth="4" fill="none" strokeLinecap="round" />
          <circle cx="76" cy="55" r="6" fill="#2d2a45" />
          <circle cx="78" cy="53" r="2.2" fill="#fff" />
        </>
      ) : (
        <>
          <circle cx="45" cy="55" r="6" fill="#2d2a45" />
          <circle cx="76" cy="55" r="6" fill="#2d2a45" />
          <circle cx="47" cy="53" r="2.2" fill="#fff" />
          <circle cx="78" cy="53" r="2.2" fill="#fff" />
        </>
      )}

      {/* cheeks */}
      <circle cx="31" cy="70" r="6" fill="#ff9db5" opacity="0.55" />
      <circle cx="89" cy="70" r="6" fill="#ff9db5" opacity="0.55" />

      {/* accessories */}
      {mood === 'shield' && (
        <g transform="translate(83 78)">
          <path d="M0 -14 L13 -9 V2 Q13 12 0 17 Q-13 12 -13 2 V-9 Z"
            fill="#ffc93c" stroke="#2d2a45" strokeWidth="3" strokeLinejoin="round" />
          <path d="M-5 1 l4 4 l7 -8" stroke="#2d2a45" strokeWidth="3" fill="none"
            strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )}
      {mood === 'star' && (
        <path d="M96 22 l3.6 7.4 8.1 1.2 -5.9 5.7 1.4 8.1 -7.2-3.8 -7.2 3.8 1.4-8.1 -5.9-5.7 8.1-1.2z"
          fill="#ffc93c" stroke="#2d2a45" strokeWidth="2.5" strokeLinejoin="round" />
      )}
      {mood === 'think' && (
        <g>
          <circle cx="98" cy="28" r="5" fill="#fff" stroke="#2d2a45" strokeWidth="2.5" />
          <circle cx="108" cy="18" r="8" fill="#fff" stroke="#2d2a45" strokeWidth="2.5" />
          <text x="108" y="23" textAnchor="middle" fontSize="11" fontWeight="700" fill="#2d2a45">?</text>
        </g>
      )}
      {mood === 'cheer' && (
        <g stroke="#ffc93c" strokeWidth="4" strokeLinecap="round">
          <path d="M14 44 L4 38" />
          <path d="M16 30 L9 22" />
          <path d="M106 44 L116 38" />
          <path d="M104 30 L111 22" />
        </g>
      )}
    </svg>
  );
}
